import React, { useContext, useMemo, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { CHILD_CLASSIFICATIONS } from "./Type";
import { notifError, notifSuccess } from "../../actions/FlashMessageActions";
import MediaAPI from "../../api/MediaAPI";
import ContentContext from "../../contexts/ContentContext";
import MasterMediaContext from "../../contexts/MasterMediaContext";
import HostedMedia from "../../models/HostedMedia";
import CreateNewChildMedia from "../contentTabs/media/CreateNewChildMedia";
import VerticalTabs from "../contentTabs/media/MasterVerticalTabs";

import type { JSX } from "react";

import type { ContentContextValues } from "./ContentTabs";
import type { Query } from "../../api/BaseAPI";
import type {
  ChildClassification,
  GetMediaResponse,
  MediaChildren,
} from "../../api/MediaAPI.types";
import type {
  ChildClassificationItem,
  ChildMedia,
  MasterMediaContextValues,
} from "../../contexts/MasterMediaContext";
import type { AppState } from "../../reducers";

interface TabsMasterProps {
  children: React.ReactNode;
}

interface WithKey {
  key: string | undefined;
}

interface CreateChildMediaResponse {
  uuid: string;
  translation_uuid: string;
}

const mainMediaFirst = (a: WithKey, b: WithKey): number => {
  if (a.key === CHILD_CLASSIFICATIONS.MAIN) return -1;
  if (b.key === CHILD_CLASSIFICATIONS.MAIN) return 1;
  return 0;
};

function TabsMaster({ children }: TabsMasterProps): JSX.Element {
  const { t } = useTranslation("editor-content");
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const currentLanguage = useSelector(
    (state: AppState) => state.languages.current,
  );

  const createChildMediaMutation = useMutation<
    CreateChildMediaResponse,
    Error,
    { newMedia: HostedMedia; masterId: string }
  >(
    async ({ newMedia, masterId }) =>
      MediaAPI.createChildMedia({
        masterId,
        newMedia,
      }) as Promise<CreateChildMediaResponse>,
  );

  const deleteChildMediaMutation = useMutation<unknown, Error, string>(
    async (mediaId) => MediaAPI.deleteChildMedia(mediaId),
  );

  const { item: masterItem, onItemUpdate } =
    useContext<ContentContextValues>(ContentContext);

  const getChildClassificationListQuery = MediaAPI.generateQuery({
    fn: MediaAPI.getChildClassificationList,
    params: [],
  });

  const { data: childClassificationData } = useQuery<
    ChildClassification[],
    Error
  >(
    getChildClassificationListQuery as unknown as Query<
      ChildClassification[],
      ChildClassification[]
    >,
  );

  const childClassificationList: ChildClassificationItem[] | undefined =
    childClassificationData
      ?.map((classification) => ({
        key: classification.key,
        uuid: classification.uuid,
        classificationLabel: t(classification.key),
      }))
      .sort(mainMediaFirst);

  const childMediaList: ChildMedia[] | undefined = masterItem.children
    ?.map((child) => ({
      key: child.child_classification?.key,
      uuid: child.uuid,
      label:
        (child.translations?.find((tr) => tr.default)?.name ?? child.label) ||
        t(child.child_classification?.key),
      classificationLabel: t(child.child_classification?.key),
    }))
    ?.sort(mainMediaFirst);

  const [activeTab, setActiveTab] = useState<string>(
    childMediaList?.[0]?.uuid ?? "",
  );

  const addChildMedia = async (
    mediaClassification: ChildClassification,
  ): Promise<void> => {
    const masterId = masterItem.uuid;
    const previousActiveTab = activeTab;
    const previousChildren = masterItem.children;

    const newMedia = new HostedMedia({
      classification: mediaClassification,
      label: t(mediaClassification.key),
      languageId: currentLanguage.uuid,
    });

    onItemUpdate({
      ...masterItem,
      children: [...previousChildren, newMedia],
    } as GetMediaResponse);

    setActiveTab("temp_id");

    try {
      const { uuid, translation_uuid: translationId } =
        await createChildMediaMutation.mutateAsync({
          newMedia,
          masterId,
        });

      const updatedChild = new HostedMedia({
        item: newMedia as unknown as MediaChildren,
        languageId: currentLanguage.uuid,
        classification: mediaClassification,
      })
        .setId(uuid)
        .setTranslationId(translationId);

      // HostedMedia.newTranslations() leaves language as "". Set the real code
      // so MediaTranslations' form initialises the language tab correctly.
      updatedChild.translations[0].language = currentLanguage.language;

      const getMasterMediaQueryKey = MediaAPI.generateQuery({
        fn: MediaAPI.getMedia,
        params: [masterId],
      }).queryKey;

      const updatedItem = {
        ...masterItem,
        children: [...previousChildren, updatedChild],
      } as GetMediaResponse;

      onItemUpdate(updatedItem);

      queryClient.setQueryData(getMasterMediaQueryKey, updatedItem);

      setActiveTab(uuid);
    } catch (error) {
      setActiveTab(previousActiveTab);

      onItemUpdate({
        ...masterItem,
        children: previousChildren,
      });

      dispatch(notifError(t("mediaCreatedError")));
      console.error("Error creating child media:", error);
    }
  };

  const deleteChildMedia = async (uuid: string): Promise<void> => {
    const previousActiveTab = activeTab;
    const previousChildren = masterItem.children;

    const updatedChildren = previousChildren.filter(
      (child) => child.uuid !== uuid,
    );

    const previousIndex = previousChildren.findIndex(
      (child) => child.uuid === uuid,
    );

    if (previousIndex === -1) return;

    const wasMainMedia =
      previousChildren[previousIndex]?.child_classification?.key ===
      CHILD_CLASSIFICATIONS.MAIN;

    const nextActiveTabIndex = wasMainMedia
      ? 0
      : Math.max(previousIndex - 1, 0);

    setActiveTab(updatedChildren[nextActiveTabIndex]?.uuid ?? "");

    onItemUpdate({
      ...masterItem,
      children: updatedChildren,
    });

    try {
      await deleteChildMediaMutation.mutateAsync(uuid);
      dispatch(notifSuccess(t("mediaDeleteSuccess")));
    } catch {
      onItemUpdate({
        ...masterItem,
        children: previousChildren,
      });
      setActiveTab(previousActiveTab);

      dispatch(notifError(t("mediaDeleteError")));
    }
  };

  const contextValue: MasterMediaContextValues = useMemo(
    () => ({
      childMediaList: childMediaList ?? [],
      childClassificationList,
      activeTab,
      setActiveTab,
      addChildMedia,
      deleteChildMedia,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [childMediaList, childClassificationList, activeTab],
  );

  return (
    <MasterMediaContext.Provider value={contextValue}>
      {childMediaList?.length ? (
        <VerticalTabs>{children}</VerticalTabs>
      ) : (
        <CreateNewChildMedia text={t("addFirstMedia")} />
      )}
    </MasterMediaContext.Provider>
  );
}

export default TabsMaster;
